import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FolderOpen, FileText, Clock, DollarSign, Crown, HelpCircle, RefreshCw, Plus, ArrowRight, AlertTriangle, Zap } from "lucide-react";
import { injectUserStyles, statusBadgeClass, fmtStatus } from "../../userStyles";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const STYLE_ID = "ind-dash-styles-v2";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .ind-plan-card {
      background:#0c1118; border-radius:10px;
      padding:20px 24px; display:flex; align-items:center; justify-content:space-between;
      gap:16px; flex-wrap:wrap; margin-bottom:24px;
    }
    .ind-plan-name {
      font-family:'DM Sans',sans-serif;
      font-size:1.6rem; letter-spacing:.04em; color:#fff; line-height:1;
    }
    .ind-plan-label {
      font-family:'DM Sans',sans-serif;
      font-size:9px; letter-spacing:.04em;
      color:rgba(255,255,255,.4); margin-bottom:4px;
    }
    .ind-trial-badge {
      display:inline-flex; align-items:center; gap:5px;
      padding:3px 10px; border-radius:20px;
      font-family:'DM Sans',sans-serif; font-size:10px;
      letter-spacing:.04em; font-weight:700;
      background:rgba(255,255,255,.12); color:#ffffff;
      border:1px solid rgba(255,255,255,.25); margin-top:6px;
    }
    .ind-trial-badge.warning {
      background:rgba(239,68,68,.15); color:#fca5a5;
      border-color:rgba(239,68,68,.35);
      animation: ind-pulse 1.8s ease-in-out infinite;
    }
    @keyframes ind-pulse {
      0%,100% { opacity:1; }
      50%      { opacity:.65; }
    }
    .ind-trial-banner {
      border-radius:10px; padding:14px 20px;
      display:flex; align-items:center; justify-content:space-between;
      gap:14px; flex-wrap:wrap; margin-bottom:24px;
      border:1px solid;
    }
    .ind-trial-banner.active {
      background:rgba(0,0,0,.04);
      border-color:rgba(0,0,0,.12);
    }
    .ind-trial-banner.warning {
      background:rgba(239,68,68,.06);
      border-color:rgba(239,68,68,.3);
    }
    .ind-trial-banner.expired {
      background:rgba(239,68,68,.09);
      border-color:rgba(239,68,68,.4);
    }
    .ind-plan-card.expired {
      border:1px solid rgba(239,68,68,.35);
    }
    @media (max-width:768px) {
      .up-kpi-grid { grid-template-columns:repeat(2,1fr) !important; }
      .up-kpi { padding:12px 14px !important; }
      .up-kpi-val { font-size:1.6rem !important; }
      .up-kpi-lbl { font-size:11px !important; }
      .up-kpi-sub { display:none !important; }
    }
  `;
  document.head.appendChild(el);
}

export default function IndividualDashboard() {
  const isMobile = useIsMobile();
  const [loading, setLoading]   = useState(true);
  const [projects, setProjects] = useState({ total: 0, active: 0, draft: 0, archived: 0 });
  const [quotes, setQuotes]     = useState({ total: 0 });
  const [billing, setBilling]   = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { injectLocal(); }, []);

  async function load() {
    setLoading(true);
    try {
      const pr = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, { credentials: "include" });
      if (pr.ok) {
        const d = await pr.json();
        const items = d.items || [];
        setProjects({
          total:    items.length,
          active:   items.filter((x) => x.status === "active").length,
          draft:    items.filter((x) => x.status === "draft").length,
          archived: items.filter((x) => x.status === "archived").length,
        });
      }
      try {
        const qr = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes?limit=100`, { credentials: "include" });
        if (qr.ok) { const qd = await qr.json(); setQuotes({ total: (qd.items || []).length }); }
      } catch {}
      const br = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/me`, { credentials: "include" });
      if (br.ok) setBilling(await br.json());
      setLastUpdated(new Date());
    } catch {
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const isTrialing      = billing?.trial?.isActive === true;
  const isExpiredTrial  = billing?.trial?.isExpired === true && !billing?.hasActiveSubscription;
  const trialEndsAt     = billing?.trial?.trialEndsAt ? new Date(billing.trial.trialEndsAt) : null;
  const trialDaysLeft   = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / 86_400_000))
    : null;
  const trialWarning    = trialDaysLeft !== null && trialDaysLeft <= 1; // ≤1 day → warning

  const planName = isExpiredTrial
    ? "Trial Expired"
    : isTrialing
      ? "Free Trial"
      : billing?.subscription?.plan?.name || "Free";

  const KPIS = [
    { accent: "#0c1118", val: projects.total,  lbl: "Total Projects", sub: "All time"     },
    { accent: "#111111", val: projects.active,  lbl: "Active",         sub: "In progress"  },
    { accent: "#3b82f6", val: projects.draft,   lbl: "Drafts",         sub: "Not yet active" },
    { accent: "#6366f1", val: quotes.total,     lbl: "Quotes",         sub: "Submitted"    },
  ];

  const QUICK_ACTIONS = [
    { to: "/dashboard/projects",    icon: FolderOpen, name: "Projects",        desc: "View and manage" },
    { to: "/dashboard/quotes",      icon: DollarSign, name: "Quotes",          desc: "Create & track" },
    { to: "/dashboard/estimator",   icon: FileText,   name: "Estimator",       desc: "Run estimations" },
    { to: "/dashboard/subscription",icon: Crown,      name: "Subscription",    desc: "Plans & billing" },
    { to: "/dashboard/engineering-plans", icon: FileText, name: "Eng. Plans",  desc: "Download PDFs" },
    { to: "/dashboard/support",     icon: HelpCircle, name: "Support",         desc: "Get assistance" },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <div className="up-title">Dashboard</div>
          <div className="up-sub">
            Welcome back &nbsp;·&nbsp;
            {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : "Loading…"}
          </div>
        </div>
        <button className="up-btn up-btn-outline" onClick={load} disabled={loading} title="Refresh">
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} />
          {!isMobile && "Refresh"}
        </button>
      </div>


      {/* Trial warning banner — shown above the plan card when trial expires within 1 day */}
      {isTrialing && trialWarning && (
        <div className="ind-trial-banner warning">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AlertTriangle size={18} style={{ color: "#f87171", flexShrink: 0 }} />
            <div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "14px", fontWeight: 600, color: "#fca5a5" }}>
                {trialDaysLeft === 0 ? "Your free trial expires today" : "Your free trial expires tomorrow"}
              </div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: "rgba(252,165,165,.75)", marginTop: 2 }}>
                Subscribe now to keep access to all features.
              </div>
            </div>
          </div>
          <Link to="/dashboard/subscription" className="up-btn" style={{
            background: "#ef4444", color: "#fff", border: "none",
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 8,
            fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 600,
            textDecoration: "none", flexShrink: 0,
          }}>
            <Zap size={13} /> Upgrade Now
          </Link>
        </div>
      )}

      {/* Plan card */}
      <div className={`ind-plan-card${isExpiredTrial ? " expired" : ""}`}>
        <div>
          <div className="ind-plan-label">Current Plan</div>
          <div className="ind-plan-name" style={isExpiredTrial ? { color: "#fca5a5" } : undefined}>{planName}</div>
          {isExpiredTrial && trialEndsAt && (
            <div className="ind-trial-badge warning">
              <Clock size={10} />
              {`Expired ${trialEndsAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
            </div>
          )}
          {isTrialing && trialEndsAt && (
            <div className={`ind-trial-badge${trialWarning ? " warning" : ""}`}>
              <Clock size={10} />
              {trialDaysLeft === 0
                ? "Expires today"
                : trialDaysLeft === 1
                  ? "1 day remaining"
                  : `${trialDaysLeft} days remaining`}
              {" · "}
              {trialEndsAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </div>
        <Link to="/dashboard/subscription" className="up-btn" style={{ background: "#ffffff", color: "#111111" }}>
          <Crown size={14} /> {(isTrialing || isExpiredTrial) ? "Upgrade Plan" : "Manage Plan"}
        </Link>
      </div>

      {/* KPIs */}
      <div className="up-kpi-grid" style={{ gridTemplateColumns: "repeat(2,1fr)" }}>
        {KPIS.map((k) => (
          <div className="up-kpi" key={k.lbl} style={isMobile ? { padding: "12px 14px" } : undefined}>
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val" style={isMobile ? { fontSize: "1.6rem" } : undefined}>{loading ? "—" : k.val}</div>
            <div className="up-kpi-lbl">{k.lbl}</div>
            {!isMobile && <div className="up-kpi-sub">{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Two-col body */}
      <div className="up-two-col">
        {/* Quick actions */}
        <div className="up-panel">
          <div className="up-panel-head">
            <div className="up-panel-title">Quick Actions</div>
          </div>
          {QUICK_ACTIONS.map((a) => (
            <Link to={a.to} className="up-qa-link" key={a.to}>
              <div className="up-qa-icon"><a.icon size={16} /></div>
              <div style={{ flex: 1 }}>
                <div className="up-qa-name">{a.name}</div>
                <div className="up-qa-desc">{a.desc}</div>
              </div>
              <ArrowRight size={15} className="up-qa-arrow" />
            </Link>
          ))}
        </div>

        {/* CTA + project summary */}
        <div>
          <div className="up-cta-banner" style={{ marginBottom: "20px" }}>
            <div>
              <div className="up-cta-title">Start a New Project</div>
              <div className="up-cta-sub">Kick off your next design in seconds.</div>
            </div>
            <button className="up-btn" style={{ background: "#ffffff", color: "#000000" }} onClick={() => navigate("/dashboard/projects?intent=new")}>
              <Plus size={14} /> New Project
            </button>
          </div>

          <div className="up-panel">
            <div className="up-panel-head">
              <div className="up-panel-title">Project Breakdown</div>
              <Link to="/dashboard/projects" className="up-panel-link">View all <ArrowRight size={12} /></Link>
            </div>
            {[
              { label: "Active",   value: projects.active,   accent: "#10b981" },
              { label: "Drafts",   value: projects.draft,    accent: "#9ca3af" },
              { label: "Archived", value: projects.archived, accent: "#9ca3af" },
            ].map((row) => (
              <div className="up-list-row" key={row.label}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: row.accent, flexShrink: 0 }} />
                  <span className="up-list-row-name">{row.label}</span>
                </div>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "1.3rem", color: "#0c1118" }}>
                  {loading ? "—" : row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}